import { motion, type MotionStyle } from 'motion/react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { cn } from 'v5/platform/dom/cn';
import { keepAnimationInJs } from 'v5/platform/react/keepAnimationInJs';
import { TileAurora } from './TileAurora';

/**
 * Lets what a layer shows hold its fade-in until it is ready (a picture
 * until it has loaded); `hold` returns the release.
 */
const LayerContext = createContext<{ hold(): () => void } | null>(null);

/**
 * Holds the layer this renders in from fading in until the returned release
 * is called (a picture's load or error); outside a layer it does nothing.
 */
export function useMediaLayerHold(): () => void {
  const layer = useContext(LayerContext);
  const release = useRef<(() => void) | null>(null);
  /** What it waited for came: an effect run again (Fast Refresh) gets no second load. */
  const came = useRef(false);
  useLayoutEffect(() => {
    if (!layer || came.current) return;
    release.current = layer.hold();
    return release.current;
  }, [layer]);
  return useCallback(() => {
    came.current = true;
    release.current?.();
  }, []);
}

/** Holds its layer for as long as it is shown: what the layer will show hasn't come yet. */
export function MediaLayerWait() {
  useMediaLayerHold();
  return null;
}

export type MediaLayerEntry<T> = {
  /**
   * Of this layer, not of its subject: a subject shown again gets a layer of
   * its own, which fades in, however much of the earlier one is still there.
   */
  id: number;
  subject: string;
  /** Under the content and the wait's aurora, never dimmed (a backdrop). */
  under: ReactNode;
  content: ReactNode;
  /** The host's own, made with the layer (a swipe's offset). */
  data: T;
  /** The subject shown; any other stays until the current one covers it. */
  current: boolean;
};

let lastLayerId = 0;

/**
 * The layers for what is shown as `subject` changes: a new subject's layer
 * goes on top and fades in whole once ready, over the ones before, which
 * stay as they are until it covers them, so the view never shows through
 * however fast the subjects change. A layer that never got ready never
 * fades in later over a subject shown after it. On mounting, an empty layer
 * stands for the one shown, so the first wait looks like any other.
 */
export function useMediaLayerStack<T>({
  subject,
  under,
  content,
  createData
}: {
  subject: string;
  under?: ReactNode;
  content: ReactNode;
  createData: () => T;
}) {
  type Entry = Omit<MediaLayerEntry<T>, 'current'>;
  const entry = (
    entrySubject: string,
    entryUnder: ReactNode,
    entryContent: ReactNode
  ): Entry => ({
    id: ++lastLayerId,
    subject: entrySubject,
    under: entryUnder,
    content: entryContent,
    data: createData()
  });
  // Not AnimatePresence: it drops leaving layers only all together, so the
  // one still covering the view would go with the rest.
  const [layers, setLayers] = useState<Entry[]>(() => [
    entry('', null, null),
    entry(subject, under, content)
  ]);
  /** Ids of the layers that have been ready. */
  const [ready, setReady] = useState<ReadonlySet<number>>(new Set());
  const shown = useRef({ under, content });
  useLayoutEffect(() => {
    shown.current = { under, content };
  });
  const top = layers[layers.length - 1];
  if (top.subject !== subject) {
    // The one leaving keeps what it showed last; a subject shown again gets
    // a new layer on top (the earlier one stays below until that covers it).
    const next = entry(subject, under, content);
    setLayers(current => [
      ...current
        .filter(layer => ready.has(layer.id))
        .map(layer =>
          layer.id === top.id ? { ...layer, ...shown.current } : layer
        ),
      next
    ]);
  }
  return {
    layers: layers.map(
      (layer): MediaLayerEntry<T> =>
        layer === top
          ? { ...layer, under, content, current: true }
          : { ...layer, current: false }
    ),
    top,
    /** The current subject's layer waits for what it shows. */
    loading: !ready.has(top.id),
    onReady: (id: number) => setReady(current => new Set(current).add(id)),
    /** Drops the layers under one that has faded in fully. */
    onSettled: (id: number) =>
      setLayers(current => {
        const index = current.findIndex(layer => layer.id === id);
        return index < 0 ? current : current.slice(index);
      })
  };
}

/**
 * One layer of `useMediaLayerStack`: fades in once what it shows has stopped
 * holding it. One that is no longer current dims after a beat (a cached
 * subject covers it first, so nothing flickers), an aurora coming up under
 * its content while the next one waits, and is out of reach of Tab and
 * screen readers.
 */
export function MediaLayer({
  layer,
  onReady,
  onSettled,
  className,
  auroraClassName,
  contentClassName,
  contentStyle,
  auroraOverContent = false,
  instant = false
}: {
  layer: MediaLayerEntry<unknown>;
  onReady(): void;
  onSettled(): void;
  /** The layer's box: it covers the view. */
  className?: string;
  /** The aurora's colour and strength (`text-*`, `--fx-intensity`), its blend. */
  auroraClassName?: string;
  /** Places the content (the part that dims) in the layer. */
  contentClassName?: string;
  contentStyle?: MotionStyle;
  /**
   * The aurora over the content instead of under it: for content that fills the layer (a
   * picture without a backdrop), which leaves nowhere around it for the aurora to show. The
   * content then dims deeper while it waits, so the aurora's light shows on it.
   */
  auroraOverContent?: boolean;
  /** Shown at once, not faded in from nothing: what it holds is already in memory (a local copy). */
  instant?: boolean;
}) {
  const [holds, setHolds] = useState(0);
  // What the layer shows takes its holds as it mounts, before the layer
  // counts as ready: until then it isn't.
  const [mounted, setMounted] = useState(false);
  useLayoutEffect(() => setMounted(true), []);
  /** It has been ready: what comes into it later (a backdrop a larger box needs) doesn't hide it again. */
  const shown = useRef(false);
  const [hold] = useState(() => ({
    hold() {
      if (shown.current) return () => {};
      setHolds(count => count + 1);
      let released = false;
      return () => {
        if (released) return;
        released = true;
        setHolds(count => count - 1);
      };
    }
  }));
  const ready = mounted && holds === 0;
  const latestOnReady = useRef(onReady);
  useLayoutEffect(() => {
    latestOnReady.current = onReady;
  });
  useEffect(() => {
    if (!ready) return;
    shown.current = true;
    latestOnReady.current();
  }, [ready]);
  const aurora = !layer.current && (
    // Under the content, over what is under it, the long wait shows around
    // it (or over a content that fills the layer). A blend sits here: the
    // fade makes this a stacking context, so one on the content would only
    // mix with this layer.
    <motion.div
      data-aurora=""
      className={cn('absolute inset-0', auroraClassName)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      // With the content's dimming: the same beat, the same pace.
      transition={{ delay: 0.5, duration: 1.5, ease: 'easeInOut' }}
      onUpdate={keepAnimationInJs}
    >
      <TileAurora />
    </motion.div>
  );
  return (
    <motion.div
      // React 18 has no `inert` prop: the attribute goes through as a string.
      {...(layer.current ? {} : { inert: '' })}
      aria-hidden={!layer.current || undefined}
      className={cn('absolute inset-0 isolate', className)}
      initial={{ opacity: instant ? 1 : 0 }}
      animate={{ opacity: ready || instant ? 1 : 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      onUpdate={keepAnimationInJs}
      onAnimationComplete={() => {
        if (ready) onSettled();
      }}
    >
      <LayerContext.Provider value={hold}>
        {layer.under}
        {!auroraOverContent && aurora}
        <motion.div
          data-dimmed={!layer.current || undefined}
          className={cn(
            'transition-[filter] duration-200',
            !layer.current && 'saturate-50 delay-500 duration-1500 ease-in-out',
            // the aurora lies over the content: a deeper dim, the dark it glows on, as the
            // dialog's backdrop gives it
            !layer.current &&
              (auroraOverContent ? 'brightness-50' : 'brightness-75'),
            contentClassName
          )}
          style={contentStyle}
        >
          {layer.content}
        </motion.div>
        {auroraOverContent && aurora}
      </LayerContext.Provider>
    </motion.div>
  );
}
